import { useState, useCallback, useContext, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import paymentApi from 'api/rest/payment';
import EchoContext from 'context/EchoContext';
import uiSlice from 'store/modules/ui';
import authSlice from 'store/modules/auth';

/**
 * Hook para crear y gestionar suscripciones recurrentes con Wompi.
 *
 * Flujo de alta:
 *   startSubscription({ planId, card })
 *   → tokeniza la tarjeta contra Wompi (llave pública, PAN no toca el backend)
 *   → POST /api/wompi/subscriptions (crea payment_source + primer cobro)
 *   → si el cobro fue aprobado de inmediato: estado "success"
 *   → si quedó PENDING: espera el evento Pusher .payment.completed / .payment.failed
 *
 * Estados: idle | processing | success | failed
 */
function useSubscription(userId) {
  const dispatch = useDispatch();
  const echoContext = useContext(EchoContext);
  const echo = echoContext?.echo || null;

  const [modalOpen, setModalOpen] = useState(false);
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState(null);

  const channelRef = useRef(null);
  const timeoutRef = useRef(null);

  // Sincroniza "processing" con Redux para silenciar notificaciones duplicadas.
  useEffect(() => {
    dispatch(uiSlice.actions.setPaymentModalProcessing(modalOpen && status === 'processing'));
  }, [modalOpen, status, dispatch]);

  // Marca éxito y refresca el usuario (membresía + sesiones).
  const markSuccess = useCallback((eventData) => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    const sessionsToAdd = eventData?.total_sessions_paid || eventData?.sessions_paid || 0;
    if (eventData?.membership_status || sessionsToAdd) {
      dispatch(authSlice.actions.meSuccess({
        membership_status: eventData.membership_status,
        available_sessions: sessionsToAdd,
      }));
    }
    setStatus('success');
    dispatch(authSlice.actions.me('platform'));
  }, [dispatch]);

  // Escucha confirmación asíncrona cuando el primer cobro quedó PENDING.
  useEffect(() => {
    if (!echo || !userId || !modalOpen || status !== 'processing') return undefined;
    if (channelRef.current) return undefined;
    if (!echo.private || typeof echo.private !== 'function') return undefined;

    let channel;
    try {
      channel = echo.private(`users.${userId}`);
      channelRef.current = channel;
    } catch {
      return undefined;
    }

    const onCompleted = (eventData) => markSuccess(eventData);
    const onFailed = () => {
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      setStatus('failed');
    };
    channel.listen('.payment.completed', onCompleted);
    channel.listen('.payment.failed', onFailed);

    return () => {
      if (channelRef.current) {
        try {
          channelRef.current.stopListening('.payment.completed', onCompleted);
          channelRef.current.stopListening('.payment.failed', onFailed);
          channelRef.current = null;
        } catch {
          // silent
        }
      }
    };
  }, [echo, userId, modalOpen, status, markSuccess]);

  /** Abre el modal en modo formulario (para capturar la tarjeta antes de procesar). */
  const openModal = useCallback(() => {
    setErrorMessage(null);
    setStatus('idle');
    setModalOpen(true);
  }, []);

  /**
   * Crea la suscripción. Con `card` tokeniza una tarjeta nueva; con `paymentSourceId`
   * reusa la tarjeta guardada (sin teclear ni tokenizar).
   * @param {{ planId: number, card?: object, paymentSourceId?: string, couponId?: number }} args
   */
  const startSubscription = useCallback(async ({ planId, card, paymentSourceId, couponId }) => {
    setModalOpen(true);
    setStatus('processing');
    setErrorMessage(null);

    try {
      let payload;
      if (paymentSourceId) {
        // Reusar tarjeta guardada: no se tokeniza ni se piden datos de tarjeta.
        payload = { plan_id: planId, payment_source_id: paymentSourceId };
      } else {
        const tokens = await paymentApi.getAcceptanceTokens();
        const token = await paymentApi.tokenizeCard(tokens.public_key, card);
        payload = {
          plan_id: planId,
          card_token: token.id,
          acceptance_token: tokens.acceptance_token,
          accept_personal_auth: tokens.accept_personal_auth,
        };
      }

      // Cupón validado en el front (el backend lo RE-valida en SubscriptionManager).
      if (couponId) payload.coupon_id = couponId;

      const subscription = await paymentApi.createSubscription(payload);

      if (subscription?.status === 'active') {
        markSuccess({ membership_status: 'ACTIVE' });
        return;
      }

      // Quedó pendiente: esperar confirmación por webhook (con timeout de seguridad de 90s).
      timeoutRef.current = setTimeout(() => setStatus('failed'), 90000);
    } catch (error) {
      setErrorMessage(error?.message || 'No pudimos procesar la suscripción.');
      setStatus('failed');
    }
  }, [markSuccess]);

  const closeModal = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    setModalOpen(false);
    setTimeout(() => {
      setStatus('idle');
      setErrorMessage(null);
    }, 300);
  }, []);

  /* --------------------------- Gestión existente ---------------------------- */

  const fetchMySubscription = useCallback(() => paymentApi.getMySubscription(), []);

  const fetchSavedCard = useCallback(() => paymentApi.getSavedCard(), []);

  const fetchMyCharges = useCallback((params) => paymentApi.getMySubscriptionCharges(params), []);

  const cancelSubscription = useCallback((id) => paymentApi.cancelSubscription(id), []);

  const retryPayment = useCallback((id) => paymentApi.retrySubscriptionPayment(id), []);

  const updateCard = useCallback(async (id, card) => {
    const tokens = await paymentApi.getAcceptanceTokens();
    const token = await paymentApi.tokenizeCard(tokens.public_key, card);
    return paymentApi.updateSubscriptionCard(id, {
      card_token: token.id,
      acceptance_token: tokens.acceptance_token,
      accept_personal_auth: tokens.accept_personal_auth,
    });
  }, []);

  return {
    modalOpen,
    status,
    errorMessage,
    openModal,
    startSubscription,
    closeModal,
    fetchMySubscription,
    fetchSavedCard,
    fetchMyCharges,
    cancelSubscription,
    retryPayment,
    updateCard,
  };
}

export default useSubscription;
